import React from 'react';

const BlogPreview = ({ blogData, isOpen, onClose }) => {
  if (!isOpen) return null;

  // Debug logging to help troubleshoot data structure
  console.log('BlogPreview - blogData:', blogData);
  console.log('BlogPreview - tags type:', typeof blogData.tags, 'value:', blogData.tags);
  console.log('BlogPreview - featuredImage:', blogData.featuredImage);
  console.log('BlogPreview - gallery:', blogData.gallery);
  console.log('BlogPreview - beforeImage:', blogData.beforeImage);
  console.log('BlogPreview - afterImage:', blogData.afterImage);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Blog Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Preview Content */}
        <div className="px-6 py-4">
          {/* Featured Image */}
          {blogData.featuredImage && (
            <div className="mb-6">
              <img
                src={typeof blogData.featuredImage === 'string' ? blogData.featuredImage : blogData.featuredImage.url}
                alt={typeof blogData.featuredImage === 'string' ? blogData.title : (blogData.featuredImage.alt || blogData.title)}
                className="w-full h-64 object-cover rounded-lg"
                onError={(e) => {
                  console.log('Image failed to load:', e.target.src);
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {blogData.title || 'Blog Title'}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center text-sm text-gray-500 mb-6 space-x-4">
            <span>By {blogData.author || 'Author'}</span>
            {blogData.projectDate && (
              <span>{formatDate(blogData.projectDate)}</span>
            )}
            {blogData.location && (
              <span>{blogData.location}</span>
            )}
          </div>

          {/* Excerpt */}
          {blogData.excerpt && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-lg text-gray-700 italic">"{blogData.excerpt}"</p>
            </div>
          )}

          {/* Intro Paragraph */}
          {blogData.introParagraph && (
            <div className="mb-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                {blogData.introParagraph}
              </p>
            </div>
          )}

          {/* Before Section */}
          {blogData.beforeSection && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Before</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    {blogData.beforeSection}
                  </p>
                </div>
                {blogData.beforeImage && (
                  <div>
                    <img
                      src={typeof blogData.beforeImage === 'string' ? blogData.beforeImage : blogData.beforeImage.url}
                      alt={typeof blogData.beforeImage === 'string' ? 'Before renovation' : (blogData.beforeImage.alt || 'Before renovation')}
                      className="w-full rounded-lg"
                      onError={(e) => {
                        console.log('Before image failed to load:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Process Section */}
          {blogData.processSection && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">The Process</h2>
              <p className="text-gray-700 leading-relaxed">
                {blogData.processSection}
              </p>
            </div>
          )}

          {/* After Section */}
          {blogData.afterSection && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">After</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    {blogData.afterSection}
                  </p>
                </div>
                {blogData.afterImage && (
                  <div>
                    <img
                      src={typeof blogData.afterImage === 'string' ? blogData.afterImage : blogData.afterImage.url}
                      alt={typeof blogData.afterImage === 'string' ? 'After renovation' : (blogData.afterImage.alt || 'After renovation')}
                      className="w-full rounded-lg"
                      onError={(e) => {
                        console.log('After image failed to load:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gallery */}
          {blogData.gallery && blogData.gallery.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Project Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {blogData.gallery.map((image, index) => (
                  <div key={index} className="aspect-square rounded-lg overflow-hidden">
                    <img
                      src={typeof image === 'string' ? image : image.url}
                      alt={typeof image === 'string' ? `Gallery image ${index + 1}` : (image.alt || `Gallery image ${index + 1}`)}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.log('Gallery image failed to load:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Client Testimonial */}
          {blogData.clientTestimonial && (
            <div className="mb-8 bg-blue-50 p-6 rounded-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Client Testimonial</h2>
              <blockquote className="text-lg text-gray-700 italic">
                "{blogData.clientTestimonial}"
              </blockquote>
            </div>
          )}

          {/* FAQ Section */}
          {blogData.faqs && blogData.faqs.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {blogData.faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          {blogData.callToActionText && (
            <div className="mb-8 bg-green-50 p-6 rounded-lg text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Ready to Start Your Project?</h2>
              <p className="text-lg text-gray-700 mb-4">
                {blogData.callToActionText}
              </p>
              {blogData.callToActionLink && (
                <a
                  href={blogData.callToActionLink}
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Get Started Today
                </a>
              )}
            </div>
          )}

          {/* Tags */}
          {blogData.tags && (
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {Array.isArray(blogData.tags) 
                  ? blogData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))
                  : blogData.tags.split(',').map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tag.trim()}
                      </span>
                    ))
                }
              </div>
            </div>
          )}

          {/* SEO Info */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Information</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-700">Meta Title:</p>
                <p className="text-gray-600">{blogData.metaTitle || 'Not set'}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Primary Keyword:</p>
                <p className="text-gray-600">{blogData.primaryKeyword || 'Not set'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="font-medium text-gray-700">Meta Description:</p>
                <p className="text-gray-600">{blogData.metaDescription || 'Not set'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPreview;
